import React, { useCallback, useMemo, useRef } from 'react';
import { isEmpty, debounce } from 'underscore';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

export function withFinalUrlRecommendationUpdate(WrappedFinalUrlComponent) {
  const HOC = (props) => {
    const {
      updateValidation,
      enableCopilotRecommendations,
      enableSpecialCharactersValidation,
    } = props;

    const currentValidation = useRef();

    const debouncedSetFinalUrl = useMemo(() => debounce((finalUrl) => {
      updateValidation(
        currentValidation.current.messages,
        { ...currentValidation.current.data, FinalUrlForRecommendation: finalUrl },
        currentValidation.current.dirtied
      );
    }, 500), [updateValidation]);

    const updateValidationInternal = useCallback((messages, data, dirtied) => {
      currentValidation.current = { messages, data, dirtied };

      const finalUrl = data && data.FinalUrls && data.FinalUrls[0];

      if (enableCopilotRecommendations) {
        // No debounce for copilot recommendations since it'll refresh recommendations when lose focus, otherwise the data might not have been set when lose focus
        updateValidation(
          messages,
          { ...data, FinalUrlForRecommendation: isEmpty(messages) && finalUrl },
          dirtied
        );
      } else {
        debouncedSetFinalUrl.cancel();
        debouncedSetFinalUrl(isEmpty(messages) && finalUrl);
        updateValidation(messages, data, dirtied);
      }
    }, [debouncedSetFinalUrl, enableCopilotRecommendations, updateValidation]);

    return (
      <WrappedFinalUrlComponent
        {...props}
        updateValidation={updateValidationInternal}
        enableSpecialCharactersValidation={enableSpecialCharactersValidation}
      />
    );
  };

  HOC.propTypes = {
    updateValidation: PropTypes.func.isRequired,
    enableCopilotRecommendations: PropTypes.bool,
    enableSpecialCharactersValidation: PropTypes.bool,
  };

  HOC.defaultProps = {
    enableCopilotRecommendations: true,
    enableSpecialCharactersValidation: false,
  };

  HOC.displayName = `withFinalUrlRecommendationUpdate(${getDisplayName(WrappedFinalUrlComponent)})`;
  hoistNonReactStatics(HOC, WrappedFinalUrlComponent);

  return HOC;
}
